import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../lib/auth';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Check, ArrowRight, Sparkles, Mail, Building2 } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

// Load Razorpay checkout.js once.
const loadRazorpay = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement('script');
  s.src = 'https://checkout.razorpay.com/v1/checkout.js';
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

const PlanCard = ({ plan, onBuy, popular, badge }) => (
  <div className={`relative panel p-8 flex flex-col ${popular ? 'border-gradient-pt' : ''}`} data-testid={`plan-card-${plan.id}`}>
    {popular && <div className="absolute -top-3 left-6 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-accent border-gradient-pt px-2 py-0.5 bg-background"><Sparkles size={10} /> Most popular</div>}
    {badge && <div className="absolute -top-3 right-6 font-mono text-[10px] uppercase tracking-widest border border-border bg-background px-2 py-0.5">{badge}</div>}
    <div className="label-mono mb-2">{plan.id.toUpperCase()}</div>
    <h3 className="text-2xl font-medium tracking-tight">{plan.name}</h3>
    <div className="mt-4 flex items-baseline gap-2">
      <span className="font-mono text-4xl">₹{plan.price_inr.toLocaleString('en-IN')}</span>
      {plan.id === 'monthly' && <span className="text-sm text-muted-foreground">/ month</span>}
    </div>
    <ul className="mt-6 space-y-2 text-sm flex-1">
      <li className="flex items-start gap-2"><Check size={14} className="mt-0.5 text-teal-500 shrink-0" strokeWidth={2} /> {plan.credits} compliance {plan.credits === 1 ? 'check' : 'checks'}</li>
      <li className="flex items-start gap-2"><Check size={14} className="mt-0.5 text-teal-500 shrink-0" strokeWidth={2} /> {plan.validity_days ? `Valid for ${plan.validity_days} days` : 'Never expires'}</li>
      <li className="flex items-start gap-2"><Check size={14} className="mt-0.5 text-teal-500 shrink-0" strokeWidth={2} /> Full Claude Sonnet 4.5 audit</li>
      <li className="flex items-start gap-2"><Check size={14} className="mt-0.5 text-teal-500 shrink-0" strokeWidth={2} /> PDF report + email share</li>
      {plan.id === 'monthly' && <li className="flex items-start gap-2"><Check size={14} className="mt-0.5 text-teal-500 shrink-0" strokeWidth={2} /> Auto-renew, cancel anytime</li>}
    </ul>
    <Button onClick={() => onBuy(plan)} className="rounded-none h-11 mt-8" data-testid={`buy-${plan.id}`}>
      {plan.id === 'monthly' ? 'Subscribe' : 'Buy now'} <ArrowRight size={14} strokeWidth={1.5} />
    </Button>
  </div>
);

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [config, setConfig] = useState(null);
  const [coupon, setCoupon] = useState('');
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    api.get('/payments/config').then(({ data }) => setConfig(data)).catch(() => {});
    loadRazorpay();
  }, []);

  const buy = async (plan) => {
    if (!user) { toast.error('Please sign in to purchase a plan.'); nav('/'); return; }
    setBuying(plan.id);
    try {
      const okScript = await loadRazorpay();
      if (!okScript) throw new Error('Razorpay script blocked');
      if (plan.kind === 'subscription') {
        const { data } = await api.post('/payments/subscription', { plan_id: plan.id });
        const rzp = new window.Razorpay({
          key: data.razorpay_key_id,
          subscription_id: data.subscription_id,
          name: 'PackCheck',
          description: `${plan.name} · 10 checks per month`,
          prefill: { email: user.email, name: user.name },
          theme: { color: '#a855f7' },
          handler: async (resp) => {
            try {
              await api.post('/payments/subscription/verify', resp);
              toast.success('Subscription active — 10 monthly credits added!');
              nav('/account');
            } catch (e) {
              toast.error(e?.response?.data?.detail || 'Verification failed');
            }
          },
          modal: { ondismiss: () => setBuying(null) },
        });
        rzp.open();
      } else {
        const { data } = await api.post('/payments/order', {
          plan_id: plan.id, coupon_code: coupon || null,
        });
        const rzp = new window.Razorpay({
          key: data.razorpay_key_id,
          order_id: data.order_id,
          amount: data.amount_inr * 100,
          currency: 'INR',
          name: 'PackCheck',
          description: `${plan.name} · ${plan.credits} ${plan.credits === 1 ? 'check' : 'checks'}`,
          prefill: { email: user.email, name: user.name },
          theme: { color: '#a855f7' },
          handler: async (resp) => {
            try {
              await api.post('/payments/verify', resp);
              toast.success(`${plan.credits} credit${plan.credits > 1 ? 's' : ''} added!`);
              nav('/account');
            } catch (e) {
              toast.error(e?.response?.data?.detail || 'Verification failed');
            }
          },
          modal: { ondismiss: () => setBuying(null) },
        });
        rzp.open();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Could not start checkout');
    } finally {
      setBuying(null);
    }
  };

  if (!config) return <Layout><div className="p-10"><div className="dot-loader"><span/><span/><span/></div></div></Layout>;

  const planById = Object.fromEntries(config.plans.map((p) => [p.id, p]));

  return (
    <Layout>
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 py-12 md:py-20" data-testid="pricing-page">
        <div className="max-w-3xl mb-12">
          <div className="label-mono mb-3">Pricing</div>
          <h1 className="text-3xl md:text-5xl font-medium tracking-tight">Pay only for the audits you need.</h1>
          <p className="mt-4 text-base md:text-lg text-muted-foreground">Every account starts with <span className="text-gradient-pt font-medium">1 free PackCheck</span>. Upgrade only when you need more.</p>
        </div>

        {/* Coupon input — affects one-time orders */}
        <div className="mb-8 flex flex-col sm:flex-row gap-3 sm:items-center">
          <Input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Coupon code (optional)" className="rounded-none h-11 max-w-xs" data-testid="coupon-input" />
          <span className="text-xs text-muted-foreground">Applies to Pay-per-check & Annual bundle at checkout.</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="plan-grid">
          {planById.payg && <PlanCard plan={planById.payg} onBuy={buy} badge="Flexible" />}
          {planById.monthly && <PlanCard plan={planById.monthly} onBuy={buy} popular />}
          {planById.annual && <PlanCard plan={planById.annual} onBuy={buy} badge="Best value" />}
          {/* Enterprise */}
          <div className="relative panel p-8 flex flex-col" data-testid="plan-card-enterprise">
            <div className="absolute -top-3 right-6 font-mono text-[10px] uppercase tracking-widest border border-border bg-background px-2 py-0.5">Talk to us</div>
            <div className="label-mono mb-2">ENTERPRISE</div>
            <h3 className="text-2xl font-medium tracking-tight">Enterprise</h3>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="font-mono text-3xl">Custom</span>
            </div>
            <ul className="mt-6 space-y-2 text-sm flex-1">
              <li className="flex items-start gap-2"><Check size={14} className="mt-0.5 text-teal-500 shrink-0" strokeWidth={2} /> Volume pricing</li>
              <li className="flex items-start gap-2"><Check size={14} className="mt-0.5 text-teal-500 shrink-0" strokeWidth={2} /> SSO + dedicated CSM</li>
              <li className="flex items-start gap-2"><Check size={14} className="mt-0.5 text-teal-500 shrink-0" strokeWidth={2} /> SLA & priority support</li>
              <li className="flex items-start gap-2"><Check size={14} className="mt-0.5 text-teal-500 shrink-0" strokeWidth={2} /> Custom regulation rules</li>
            </ul>
            <a href="mailto:sales@thereforedesign.co.in?subject=PackCheck%20Enterprise" className="mt-8 inline-flex items-center justify-center gap-2 h-11 rounded-none border border-foreground hover:bg-foreground hover:text-background transition-colors text-sm" data-testid="enterprise-contact">
              <Mail size={14} strokeWidth={1.5} /> Contact sales
            </a>
          </div>
        </div>

        <div className="mt-16 panel p-8 max-w-3xl">
          <div className="flex items-start gap-4">
            <Building2 size={24} className="text-accent shrink-0 mt-1" strokeWidth={1.5} />
            <div>
              <h3 className="text-lg font-medium">Therefore Design teammate?</h3>
              <p className="text-sm text-muted-foreground mt-2">Sign in with your <code className="font-mono text-[12px]">@thereforedesign.co.in</code> email and get <span className="text-gradient-pt font-medium">5 free audits every month</span> — no payment needed. Internal users bypass all paywalls automatically.</p>
            </div>
          </div>
        </div>

        <p className="mt-12 text-xs text-muted-foreground max-w-2xl">
          Prices in INR, inclusive of GST where applicable. Refunds processed per our <Link to="/refund-policy" className="underline">refund policy</Link>.
          By purchasing you agree to our <Link to="/terms" className="underline">terms</Link> and <Link to="/privacy" className="underline">privacy policy</Link>.
        </p>
      </div>
    </Layout>
  );
}
